import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { SlotNGController } from '../../controllers/slot-ng-controller';
import componentStyles from './tufts-accordion.css.lit';
import tuftsFormStyles from '../tufts-form/tufts-form.css.lit';
import { classMap } from 'lit/directives/class-map.js';
import { OutlineAccordion } from '../../base/outline-accordion/outline-accordion';
import { OutlineElement } from '../../base/outline-element/outline-element';

/**
 * The TuftsAccordion component
 * @element tufts-accordion
 * @extends OutlineElement
 *
 * @slot heading: The title text for the panel component.
 * @slot default slot: The main panel content, visible when the panel is open.
 * @note Removed the `alt` property, it can be used to make the accordion wrap
 *       other accordions better. See example from [Saul Ewing](https://github.com/phase2/saul-ewing-design/tree/next/src/components/se-design/se-accordion).
 *
 * @attr active
 * @attr id
 */
@customElement('tufts-accordion')
export class TuftsAccordion extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, tuftsFormStyles];
  ngSlot = new SlotNGController(this);
  /**
   * Wether the panel is active/open.
   * Controlled by parent accordion component.
   */
  @property({ type: Boolean, reflect: true, attribute: true })
  active = false;

  /**
   * Used to provided a unique ID
   * for accordion component and accessibility purposes.
   */
  @property({ type: String, reflect: true })
  id: string = Math.floor(Math.random() * 10000).toString();

  @state() private isMobile: boolean;
  @state() private hasContent: boolean = true;

  connectedCallback(): void {
    super.connectedCallback();
    // Create a MutationObserver to observe changes in the DOM
    const observer = new MutationObserver(() => {
      this.checkForCheckedInput();
      this.checkForShowAll();
    });

    // Observe changes in the entire subtree of the element
    observer.observe(this, { childList: true, subtree: true });

    const maxWidthScreen = 769;
    this.mobileMode(maxWidthScreen);
  }

  firstUpdated(): void {
    // Get the default slot
    const defaultSlot = this.shadowRoot?.querySelector(
      'slot:not([name])'
    ) as HTMLSlotElement;

    if (defaultSlot) {
      // Initial check
      this.checkSlotContent(defaultSlot);

      // Setup listener for future changes
      defaultSlot.addEventListener('slotchange', () => {
        this.checkSlotContent(defaultSlot);
      });
    }
  }

  private checkSlotContent(slot: HTMLSlotElement): void {
    // Get assigned nodes from the slot
    const assignedNodes = slot.assignedNodes({ flatten: true });

    // Check if there are any non-empty text nodes or element nodes
    this.hasContent = assignedNodes.some(node => {
      // If it's a text node, check if it has non-whitespace content
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent?.trim() !== '';
      }
      // If it's an element node, consider it as content
      return node.nodeType === Node.ELEMENT_NODE;
    });

    this.requestUpdate();
  }

  private checkForCheckedInput() {
    const ulElement = this.querySelector('ul');
    if (ulElement) {
      const checkedInput = ulElement.querySelector('li input:checked');
      this.active = !!checkedInput;
    }
  }
  private checkForShowAll() {
    const listItem = this.querySelector(
      'li[data-once="applysoftlimit"][style]'
    );
    if (listItem) {
      const displayStyle = (listItem as HTMLElement).style.display;
      this.active = displayStyle !== 'none';
    }
  }
  mobileMode(width: number) {
    const maxMobileWidth = width - 1;
    const mediaQuery = `(max-width: ${maxMobileWidth}px)`;
    const mediaQueryList = window.matchMedia(mediaQuery);
    // Set initial value to isMobile
    this.isMobile = mediaQueryList.matches;

    // update isMobile value only when screen width is crossing maxMobileWidth (1024)
    mediaQueryList.addEventListener('change', event => {
      this.isMobile = event.matches;
      this.requestUpdate();
    });
  }

  render(): TemplateResult {
    const isMobile = this.isMobile ? 'mobile' : null;
    const isActive = this.active ? 'active' : null;

    const buttonClasses = {
      'accordion-button': true,
      'mobile': this.isMobile,
      'active': this.active,
      'inactive': !this.active,
      'no-content': !this.hasContent,
    };

    const iconClasses = {
      'accordion-icon': true,
      'mobile': this.isMobile,
      'active': this.active,
    };

    // If no content, render a static header without interactive elements
    if (!this.hasContent) {
      return html` <div class="accordion-panel">
          <div class="accordion-heading">
            <div class="${classMap(buttonClasses)}" id="${this.id}-label">
              <span class="accordion-label ${isMobile}">
                <slot name="label-content"></slot>
              </span>
            </div>
          </div>
        </div>
        <hr />`;
    }

    // Regular accordion with content
    return html` <div class="accordion-panel ${isActive}">
        <div class="accordion-heading">
          <button
            class="${classMap(buttonClasses)}"
            id="${this.id}-button"
            aria-expanded=${this.active}
            aria-controls="${this.id}-info"
            @click=${this.setActive}
          >
            <span class="${classMap(iconClasses)}"> ${this.setIcon()} </span>
            <span class="accordion-label ${isMobile}">
              <slot @click="${this.passFocus}" name="label-content"> </slot>
            </span>
          </button>
        </div>
        <div
          class="accordion-content ${isMobile}"
          aria-labelledby="${this.id}-button"
          id="${this.id}-info"
          ?hidden="${!this.active}"
        >
          <slot></slot>
        </div>
      </div>
      <hr />`;
  }

  passFocus() {
    const parentButton = this.shadowRoot?.querySelector(
      '.accordion-button'
    ) as HTMLButtonElement;
    if (parentButton) {
      parentButton.focus();
    }
  }

  setActive() {
    const parentWrapper = this.parentElement as OutlineAccordion;
    const singlePanel = parentWrapper.singlePanel;
    if (singlePanel) {
      const activePanels = [...parentWrapper.panels].filter(
        panel => panel.active == true
      );

      if (activePanels.length < 1) {
        this.active = true;
      }

      if (activePanels.length > 0) {
        activePanels.map(panel => {
          if (panel.id === this.id) {
            this.active = false;
          } else {
            panel.active = false;
            this.active = true;
          }
        });
      }
    } else {
      this.active = !this.active;
    }
  }

  setIcon() {
    if (this.active) {
      return html`<span class="close">–</span> `;
    } else {
      return html`<span class="open">+</span>`;
    }
  }
}
